/**
 * `convert/sort-lines` — line sorter with 6 modes.
 *
 * Pure string-in / string-out: split on /\r?\n/, sort, rejoin with
 * '\n' (we deliberately normalise CRLF → LF on output; saving the
 * exact byte ending was a big complication for very little real
 * benefit — most diff tools accept both).
 *
 * Modes
 *   asc / desc        — Intl.Collator default (locale-aware lexical)
 *   lengthAsc / Desc  — by string length, ties broken by collator
 *   natural           — Intl.Collator with numeric: true
 *                        ("file2" < "file10")
 *   reverse           — no comparison; just reverse current order
 *
 * Options
 *   caseInsensitive   — Collator sensitivity: 'base'
 *   trim              — trim each line before comparison
 *                        (also written to the output)
 *   ignoreEmpty       — drop empty lines (after trim) before sorting
 */

export type SortMode =
  | 'asc'
  | 'desc'
  | 'lengthAsc'
  | 'lengthDesc'
  | 'natural'
  | 'reverse';

export const SORT_MODES: ReadonlyArray<SortMode> = [
  'asc',
  'desc',
  'lengthAsc',
  'lengthDesc',
  'natural',
  'reverse',
];

export const SORT_MODE_LABEL: Record<SortMode, string> = {
  asc: '升序 (A→Z)',
  desc: '降序 (Z→A)',
  lengthAsc: '长度升序',
  lengthDesc: '长度降序',
  natural: '自然排序 (file2 < file10)',
  reverse: '反转 (保持原顺序倒置)',
};

export interface SortOpts {
  caseInsensitive?: boolean;
  trim?: boolean;
  ignoreEmpty?: boolean;
}

export interface SortResult {
  result: string;
  /** Number of lines after preprocessing (post-trim / drop-empty). */
  lineCount: number;
  /** Lines dropped by `ignoreEmpty`. */
  droppedEmpty: number;
}

const COLLATOR_CACHE = new Map<string, Intl.Collator>();

function getCollator(opts: { numeric?: boolean; caseInsensitive?: boolean }): Intl.Collator {
  const key = `${opts.numeric ? 'n' : ''}|${opts.caseInsensitive ? 'i' : 's'}`;
  let c = COLLATOR_CACHE.get(key);
  if (!c) {
    c = new Intl.Collator(undefined, {
      numeric: !!opts.numeric,
      sensitivity: opts.caseInsensitive ? 'base' : 'variant',
    });
    COLLATOR_CACHE.set(key, c);
  }
  return c;
}

export function sortLines(
  input: string,
  mode: SortMode,
  opts: SortOpts = {},
): SortResult {
  if (!input) return { result: '', lineCount: 0, droppedEmpty: 0 };

  const raw = input.split(/\r?\n/);
  let lines = opts.trim ? raw.map((l) => l.trim()) : raw.slice();
  let droppedEmpty = 0;
  if (opts.ignoreEmpty) {
    const before = lines.length;
    lines = lines.filter((l) => (opts.trim ? l : l.trim()) !== '');
    droppedEmpty = before - lines.length;
  }

  if (mode === 'reverse') {
    lines.reverse();
  } else if (mode === 'lengthAsc' || mode === 'lengthDesc') {
    const tieCmp = getCollator({ caseInsensitive: opts.caseInsensitive });
    const dir = mode === 'lengthAsc' ? 1 : -1;
    lines.sort((a, b) => {
      const d = a.length - b.length;
      if (d !== 0) return dir * d;
      return tieCmp.compare(a, b);
    });
  } else {
    const c = getCollator({
      numeric: mode === 'natural',
      caseInsensitive: opts.caseInsensitive,
    });
    lines.sort((a, b) => c.compare(a, b));
    if (mode === 'desc') lines.reverse();
  }

  return {
    result: lines.join('\n'),
    lineCount: lines.length,
    droppedEmpty,
  };
}
